import React, { useState, useEffect } from "react";
import { Input, Typography, Flex, Tooltip, Col, Spin } from "antd";
import { BarChartOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { sellBoostFetch } from "../../utlifyFunction";
import SEOComparisonModal from "./SEOComparisonModal";
import PrimaryButton from "../Buttons/PrimaryButton";
import CompetitorSEOModal from "./CompetitorSEOModal";
import { toast } from "react-toastify";
import SeoPreviewCard from "../Cards/SeoPreviewCard";

const { Title, Text } = Typography;
const { TextArea } = Input;

const SEOOptimizerModal = ({ visible, onClose, selectedProduct }) => {
	const [metaData, setMetaData] = useState({});
	const [seoScore, setSeoScore] = useState(0);
	const [showComparisonModal, setShowComparisonModal] = useState(false);
	const [showFetchSEOModal, setShowFetchSEOModal] = useState(false);
	const [loading, setLoading] = useState(true);

	const [isMetaGenerateButtonLoading, setIsMetaGenerateButtonLoading] =
		useState(false);
	const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(true);

	useEffect(() => {
		if (selectedProduct) fetchMetaData();
		selectedProduct?.variants?.edges?.map((item, index) => {
			console.log(item?.node?.title);
		});
		console.log(selectedProduct?.variants?.edges);
	}, [selectedProduct]);

	const fetchMetaData = async () => {
		const data = await sellBoostFetch("POST", "/user/getProductMetaData", {
			productId: selectedProduct?.id,
		});
		if (data?.metaData) {
			setMetaData(data.metaData);
			setLoading(false);
		}
	};

	const generateMetaData = async () => {
		setIsMetaGenerateButtonLoading(true);

		const data = await sellBoostFetch("POST", "/AI/generateMetaData", {
			productDescription: selectedProduct?.description,
			existingMeta: metaData,
		});

		if (data?.status === 200) {
			setMetaData({ ...metaData, ...data?.metaData });
			toast.success("SEO Data Updated Successfully");
			setIsUpdateButtonDisabled(false);
		}
		setIsMetaGenerateButtonLoading(false);
	};

	useEffect(() => {
		setSeoScore(calculateSeoScore(metaData));
	}, [metaData]);

	const calculateSeoScore = (metaData) => {
		let score = 0;
		let suggestions = [];
		if (!metaData) return;

		// Meta Title Scoring
		if (!metaData?.metaTitle || metaData?.metaTitle.length < 10) {
			suggestions.push(
				"Increase the Meta Title length to at least 10 characters."
			);
		} else if (metaData?.metaTitle?.length <= 60) {
			score += 30;
		} else {
			suggestions.push(
				"Keep the Meta Title under 60 characters for better SEO."
			);
		}

		// Meta Description Scoring
		if (!metaData?.metaDescription || metaData?.metaDescription?.length < 50) {
			suggestions.push(
				"Increase the Meta Description length to at least 50 characters."
			);
		} else if (metaData.metaDescription.length <= 160) {
			score += 40;
		} else {
			suggestions.push(
				"Keep the Meta Description under 160 characters for better SEO."
			);
		}

		// Focus Keywords Scoring
		if (!metaData?.focusKeywords || metaData?.focusKeywords.length < 3) {
			suggestions.push(
				"Add at least 3 focus keywords for better search ranking."
			);
		} else {
			score += 20;
		}

		// URL Slug Validation
		if (!metaData?.urlSlug) {
			suggestions.push(
				"Set an SEO-friendly URL Slug using hyphens instead of spaces."
			);
		} else if (!/^[a-z0-9-]+$/.test(metaData?.urlSlug)) {
			suggestions.push(
				"Ensure the URL Slug contains only lowercase letters, numbers, and hyphens."
			);
		} else {
			score += 10;
		}

		// OG Image Validation
		if (!metaData?.ogImage) {
			suggestions.push("Add an OG Image URL to improve social media previews.");
		} else {
			score += 10;
		}

		return { score, suggestions };
	};

	const handleChange = (field, value) => {
		setIsUpdateButtonDisabled(false);
		setMetaData((prev) => ({ ...prev, [field]: value }));
	};

	const validateAndSave = async () => {
		const trimmedTitle = metaData?.metaTitle?.trim();
		const trimmedDescription = metaData?.metaDescription?.trim();
		const trimmedSlug = metaData?.urlSlug?.trim();
		const focusKeywords = metaData?.focusKeywords?.trim();

		// Required fields validation
		if (!trimmedTitle) {
			toast.error("Meta Title is required");
			return;
		}
		if (trimmedTitle.length > 70) {
			toast.error("Meta Title must be under 70 characters");
			return;
		}

		if (!trimmedDescription) {
			toast.error("Meta Description is required");
			return;
		}
		if (trimmedDescription.length > 160) {
			toast.error("Meta Description must be under 160 characters");
			return;
		}

		if (!trimmedSlug) {
			toast.error("URL Slug cannot be empty");
			return;
		}
		if (!/^[a-z0-9-]+$/.test(trimmedSlug)) {
			toast.error(
				"URL Slug can only contain lowercase letters, numbers, and hyphens"
			);
			return;
		}

		// Optional fields validation
		if (!focusKeywords) {
			toast.error("Focus Keywords cannot be empty");
			return;
		}

		setLoading(true);

		try {
			const data = await sellBoostFetch("POST", "/shopify/updateMetaData", {
				productId: selectedProduct?.id,
				metaData: {
					...metaData,
					metaTitle: trimmedTitle,
					metaDescription: trimmedDescription,
					urlSlug: trimmedSlug,
					focusKeywords,
				},
			});
			console.log(data);

			if (data?.status === 200) {
				toast.success("SEO Data Updated Successfully");
			}
		} catch (error) {
			toast.error("An error occurred while updating SEO data");
			console.error(error);
		}

		setLoading(false);
	};

	return (
		<Spin spinning={loading}>
			<div>
				<Flex justify="space-between">
					<Col span={15}>
						<Flex justify="space-between">
							<Col span={12}>
								<Flex align="center" gap={8} style={{ marginBottom: "20px" }}>
									<Title level={2} style={{ margin: 0 }}>
										SEO Optimizer
									</Title>
									<Tooltip title="Click to see comparison">
										<InfoCircleOutlined
											style={{ color: "#1890ff", cursor: "pointer" }}
											onClick={() => setShowComparisonModal(true)}
										/>
									</Tooltip>
								</Flex>
							</Col>
							<Col span={8}>
								<PrimaryButton
								width={'100%'}
									loading={isMetaGenerateButtonLoading}
									onClick={generateMetaData}
								>
									Generate Meta Data using AI
								</PrimaryButton>
							</Col>
						</Flex>
					</Col>
					<Col span={8}>
						<Flex align="center" gap={"8px"}>
							<PrimaryButton
							width={'100%'}
								icon={<BarChartOutlined />}
								handleClick={() => setShowFetchSEOModal(true)}
							>
								Fetch Competitor SEO Data
							</PrimaryButton>
							<Tooltip title="No idea? No problem! Fetch from competitors.">
								<InfoCircleOutlined
									style={{ color: "#1890ff", cursor: "pointer" }}
								/>
							</Tooltip>
						</Flex>
					</Col>
				</Flex>
				<Flex justify="space-between">
					<Col span={15}>
						<Flex vertical gap={16} span={20}>
							{[
								{
									label: "Meta Title",
									field: "metaTitle",
									tooltip:
										"Appears as the clickable link in search results. Keep under 70 characters for best SEO impact.",
									maxLength: 70,
									type: "input",
								},
								{
									label: "Meta Description",
									field: "metaDescription",
									tooltip:
										"Summarizes the page content in search results. A compelling description improves click-through rates (CTR). Keep it under 160 characters.",
									maxLength: 160,
									type: "textarea",
								},
								{
									label: "URL Slug",
									field: "urlSlug",
									tooltip:
										"SEO-friendly URL segment. Use hyphens instead of spaces and avoid special characters. If a duplicate URL slug is found, a number will be automatically appended (e.g., product-name, product-name-1, product-name-2).",
									type: "input",
								},
								{
									label: "Focus Keywords",
									field: "focusKeywords",
									tooltip:
										"Primary keywords that help search engines understand your content. Separate multiple keywords with commas.",
									type: "input",
								},

								{
									label: "OG Image URL",
									field: "ogImage",
									tooltip:
										"This image appears when the product is shared on social media platforms like Facebook and Twitter. Use a high-quality image.",
									type: "textarea",
								},
								{
									label: "H1 Product Heading",
									field: "h1ProductHeading",
									tooltip:
										"The main heading of your product page. Search engines prioritize H1 tags for indexing.",
									type: "input",
								},
							].map(({ label, field, tooltip, maxLength, type }) => (
								<Flex key={field} vertical gap={"4px"}>
									<Flex align="center" gap={8}>
										<Title level={5} style={{ margin: 0 }}>
											{label}
										</Title>
										<Tooltip title={tooltip}>
											<InfoCircleOutlined />
										</Tooltip>
									</Flex>
									{type === "textarea" ? (
										<TextArea
											maxLength={maxLength}
											rows={3}
											value={metaData[field] || ""}
											onChange={(e) => handleChange(field, e.target.value)}
											placeholder={label}
										/>
									) : (
										<Input
											maxLength={maxLength}
											value={metaData[field] || ""}
											onChange={(e) => handleChange(field, e.target.value)}
											placeholder={label}
										/>
									)}
									{maxLength && (
										<Text type="secondary">
											{metaData[field]?.length || 0}/{maxLength} characters
										</Text>
									)}
								</Flex>
							))}
						</Flex>
					</Col>
					<Col span={8}>
						<SeoPreviewCard metaData={metaData} seoScore={seoScore} />
						<Flex align="center" gap={"8px"}>
							<PrimaryButton
							width={'100%'}
								disabled={isUpdateButtonDisabled}
								handleClick={validateAndSave}
							>
								Update
							</PrimaryButton>
							<Tooltip title="Updates may take 1-2 minutes and require a few refreshes for full effect.">
								<InfoCircleOutlined
									style={{ color: "#1890ff", cursor: "pointer" }}
								/>
							</Tooltip>
						</Flex>
					</Col>
				</Flex>

				<SEOComparisonModal
					visible={showComparisonModal}
					onClose={() => setShowComparisonModal(false)}
				/>
				<CompetitorSEOModal
					visible={showFetchSEOModal}
					onClose={() => setShowFetchSEOModal(false)}
				/>
			</div>
		</Spin>
	);
};

export default SEOOptimizerModal;
